import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMXN, formatDate, CRM_COLUMNS } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function CRM() {
  const [cots, setCots] = useState([]);

  const load = async () => {
    const { data } = await api.get("/cotizaciones");
    setCots(data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const byCol = Object.fromEntries(CRM_COLUMNS.map((c) => [c.id, []]));
    for (const c of cots) {
      if (byCol[c.status]) byCol[c.status].push(c);
      else byCol["Borrador"].push(c);
    }
    return byCol;
  }, [cots]);

  const totals = useMemo(() => {
    const out = {};
    for (const col of CRM_COLUMNS) {
      const list = grouped[col.id] || [];
      out[col.id] = { count: list.length, total: list.reduce((s, x) => s + (x.total || 0), 0) };
    }
    return out;
  }, [grouped]);

  const updateStatus = async (cot, newStatus) => {
    try {
      await api.put(`/cotizaciones/${cot.id}`, { status: newStatus });
      toast.success(`${cot.folio} → ${newStatus}`);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  return (
    <div className="space-y-8" data-testid="crm-page">
      <header>
        <div className="uppercase-label">Pipeline</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">CRM de cotizaciones</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Da seguimiento a cada presupuesto: Borrador → Enviada → En seguimiento → Ganada / Perdida. Cambia el estado
          desde cada tarjeta y mantén un histórico de tu pipeline comercial.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {CRM_COLUMNS.map((col) => (
          <div key={col.id} className="bg-white border border-border rounded-lg flex flex-col" data-testid={`crm-col-${col.id}`}>
            <div className="px-4 py-3 border-b border-border" style={{ borderTopColor: col.color, borderTopWidth: 3, borderTopStyle: "solid" }}>
              <div className="flex items-center justify-between">
                <div className="font-display text-lg" style={{ color: col.color }}>{col.title}</div>
                <span className="text-xs px-2 py-0.5 rounded-full border border-border bg-[#F4F2EC]">{totals[col.id].count}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1 tabular">{formatMXN(totals[col.id].total)}</div>
            </div>
            <div className="p-3 space-y-2 min-h-[260px]">
              {(grouped[col.id] || []).length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center border border-dashed border-border rounded">Sin tarjetas</div>
              )}
              {(grouped[col.id] || []).map((c) => (
                <div key={c.id} className="border border-border rounded-md p-3 hover-lift bg-white" data-testid={`crm-card-${c.folio}`}>
                  <div className="flex items-start justify-between">
                    <Link to={`/cotizaciones/${c.id}`} className="text-[#8B5A33] tabular text-sm font-medium hover:underline">{c.folio}</Link>
                    <span className="text-[10px] text-muted-foreground">{formatDate(c.created_at)}</span>
                  </div>
                  <div className="text-sm font-medium mt-1 line-clamp-1">{c.cliente_nombre}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{c.proyecto}</div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm tabular font-medium">{formatMXN(c.total)}</div>
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{c.categoria}</span>
                  </div>
                  <div className="mt-2">
                    <Select value={c.status} onValueChange={(v) => updateStatus(c, v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`crm-status-${c.folio}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CRM_COLUMNS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id}>{opt.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
